import { Package } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { PhotoZoom } from "@/components/PhotoZoom";
import { photoThumb, photoThumbBackend, photoFull } from "@/lib/photoUrl";

// 2026-07 · Fallback handler used on <img onError>. When the R2 CDN 404s
// (thumbnail never warmed for a legacy photo), swap the ``src`` to the
// backend URL which lazily generates the PNG and pushes it back to R2.
// Guarded by ``data-fallback-tried`` so we don't loop when the backend is also
// unable to produce the thumb (broken photo_url, GridFS-only, etc.).
const swapToBackend = (photoUrl, size) => (e) => {
  const img = e.currentTarget;
  if (img.dataset.fallbackTried === "1") return;
  const backendUrl = photoThumbBackend(photoUrl, size);
  if (backendUrl && backendUrl !== img.src) {
    img.dataset.fallbackTried = "1";
    img.src = backendUrl;
  }
};

/**
 * Product thumbnail — shows the primary photo, OR both the primary photo AND the
 * 花型 (pattern) photo side-by-side when both are present.
 *
 * Mirrors the backend's Excel/PDF export layout (composed_photo_path) so users
 * see the same visual on-screen and on the exported quote. Hovering the
 * composite thumbnail pops a larger side-by-side preview.
 */
export const ProductThumb = ({
  photoUrl,
  patternPhotoUrl,
  compositePhotoUrl,
  alt,
  size = "md",
  side = "bottom",
  zoomable = true,
  className = "w-full h-full",
  fallbackIconSize = 32,
}) => {
  const hasPrimary = Boolean(photoUrl);
  const hasPattern = Boolean(patternPhotoUrl);
  const hasComposite = Boolean(compositePhotoUrl);

  // 2026-02 · Prefer the pre-generated composite: renders as a single image
  // (no client-side split) so the on-screen thumb matches exactly what the
  // quote export embeds.
  if (hasComposite) {
    return zoomable ? (
      <PhotoZoom
        src={photoThumb(compositePhotoUrl, size)}
        fallbackSrc={photoThumbBackend(compositePhotoUrl, size)}
        zoomSrc={photoFull(compositePhotoUrl)}
        alt={alt}
        side={side}
        className={`${className} object-contain`}
      />
    ) : (
      <img loading="lazy" src={photoThumb(compositePhotoUrl, size)} alt={alt} className={`${className} object-contain`} onError={swapToBackend(compositePhotoUrl, size)} />
    );
  }

  if (!hasPrimary && !hasPattern) {
    return (
      <div className={`${className} flex items-center justify-center`}>
        <Package size={fallbackIconSize} className="text-muted-foreground" />
      </div>
    );
  }

  // Only one photo — use the existing PhotoZoom (single image + hover preview).
  if (!hasPattern || !hasPrimary) {
    const url = hasPrimary ? photoUrl : patternPhotoUrl;
    return zoomable ? (
      <PhotoZoom
        src={photoThumb(url, size)}
        fallbackSrc={photoThumbBackend(url, size)}
        zoomSrc={photoFull(url)}
        alt={alt}
        side={side}
        className={`${className} object-contain`}
      />
    ) : (
      <img loading="lazy" src={photoThumb(url, size)} alt={alt} className={`${className} object-contain`} onError={swapToBackend(url, size)} />
    );
  }

  // Both photos → side-by-side composite, with a hover preview that also shows
  // both photos side-by-side at higher resolution.
  const composite = (
    <div
      className={`${className} flex items-stretch gap-0.5 cursor-zoom-in`}
      data-testid={`product-thumb-composite-${alt || ""}`}
    >
      <img
        loading="lazy"
        src={photoThumb(photoUrl, size)}
        alt={alt}
        className="w-1/2 h-full object-contain"
        onError={swapToBackend(photoUrl, size)}
      />
      <img
        loading="lazy"
        src={photoThumb(patternPhotoUrl, size)}
        alt={`${alt} pattern`}
        className="w-1/2 h-full object-contain"
        onError={swapToBackend(patternPhotoUrl, size)}
      />
    </div>
  );

  if (!zoomable) return composite;

  return (
    <HoverCard openDelay={150} closeDelay={50}>
      <HoverCardTrigger asChild>{composite}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        className="w-auto max-w-none p-1.5 bg-white border border-border shadow-lg"
        data-testid="photo-zoom-preview"
      >
        <div className="flex items-center gap-1.5">
          <img src={photoFull(photoUrl)} alt={alt} className="max-w-[300px] max-h-[400px] object-contain" />
          <img src={photoFull(patternPhotoUrl)} alt={`${alt} pattern`} className="max-w-[300px] max-h-[400px] object-contain" />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
